import { useEffect, useState } from "react";
import api from "@/lib/api";

const LaunchContext = { state: null, listeners: new Set(), fetching: null };

export function useLaunchState() {
  const [state, setState] = useState(LaunchContext.state);

  useEffect(() => {
    LaunchContext.listeners.add(setState);
    if (!LaunchContext.state && !LaunchContext.fetching) {
      LaunchContext.fetching = api.get("/launch/state")
        .then(r => {
          LaunchContext.state = r.data;
          LaunchContext.listeners.forEach(fn => fn(r.data));
        })
        .catch(() => {})
        .finally(() => { LaunchContext.fetching = null; });
    } else if (LaunchContext.state) {
      setState(LaunchContext.state);
    }
    return () => LaunchContext.listeners.delete(setState);
  }, []);

  return state;
}

function diff(target) {
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return { ended: true, d: 0, h: 0, m: 0, s: 0 };
  const totalSec = Math.floor(ms / 1000);
  return {
    ended: false,
    d: Math.floor(totalSec / 86400),
    h: Math.floor((totalSec % 86400) / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

export default function Countdown({ to, label = "Launches in", compact = false, "data-testid": testId }) {
  const [t, setT] = useState(() => diff(to));
  useEffect(() => {
    if (!to) return;
    const id = setInterval(() => setT(diff(to)), 1000);
    return () => clearInterval(id);
  }, [to]);

  if (!to) return null;

  const Box = ({ value, unit }) => (
    <div className={`flex flex-col items-center min-w-0 ${compact ? "px-2" : "px-1.5 sm:px-2 md:px-3"}`}>
      <span className={`font-display tabular-nums leading-none ${compact ? "text-2xl" : "text-[clamp(1.25rem,3.6vw,2.25rem)]"}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className={`overline mt-2 whitespace-nowrap ${compact ? "text-[10px]" : "text-[9px] sm:text-[10px]"}`}>{unit}</span>
    </div>
  );

  return (
    <div data-testid={testId} className={compact ? "inline-flex items-baseline gap-1 text-sm tabular-nums" : "inline-block w-full max-w-full"}>
      {!compact && label && <p className="overline text-center mb-3">{label}</p>}
      {compact ? (
        <span className="font-mono text-[var(--ff-primary)] font-semibold">
          {t.ended ? "00d 00h 00m 00s" : `${t.d}d ${String(t.h).padStart(2,"0")}h ${String(t.m).padStart(2,"0")}m ${String(t.s).padStart(2,"0")}s`}
        </span>
      ) : (
        <div className="flex items-stretch justify-center divide-x divide-[var(--ff-border)] w-full">
          <Box value={t.d} unit="days"/>
          <Box value={t.h} unit="hours"/>
          <Box value={t.m} unit="min"/>
          <Box value={t.s} unit="sec"/>
        </div>
      )}
    </div>
  );
}
